<script setup lang="ts">
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { followCartoon } from '@/api/cartoon'
import type { FollowCartoonDetailItem, FollowCartoonItem } from '@/api/follow'
import type { UserWorkItem } from '@/api/home/type'
import AppErrorState from '@/components/app-error-state'
import FollowCartoonSheet from '@/components/follow-cartoon-sheet'
import FollowCartoonStrip from '@/components/follow-cartoon-strip'
import LoginPrompt from '@/components/login-prompt'
import SummaryContentCard from '@/components/summary-content-card'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useFollowPage } from '@/composables/useFollowPage'
import { useRouteRefreshSignal } from '@/composables/useRouteRefreshSignal'
import { useScreenSize } from '@/composables/useScreenSize'
import { useToast } from '@/composables/useToast'
import { useThemeStore } from '@/stores/theme'
import { useUserStore } from '@/stores/user'
import { buildCartoonUrl } from '@/utils/distribute/navigation'
import { openLegacyPlayerUrl } from '@/utils/legacy-player'
import { buildSummaryContentViewUrl, resolveSummaryContentKind } from '@/utils/profile/content-card'
import { resolveSummaryContentColumnCount } from '@/utils/profile/content-grid'
import { applyDocumentTitle } from '@/utils/page-title'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import { stateImages } from '@/utils/state-images'

defineOptions({
  name: 'FollowView',
})

const router = useRouter()
const themeStore = useThemeStore()
const userStore = useUserStore()
const toast = useToast()
const runtimeBudget = getRuntimePerformanceBudget()
const { screenWidth } = useScreenSize()
const loadMoreTarget = shallowRef<HTMLElement | null>(null)
const followLoadingIds = shallowRef<string[]>([])
const {
  topCartoons,
  feedItems,
  allFollowedCartoons,
  loadingInitial,
  loadingMore,
  loadingAllCartoons,
  loadingMoreAllCartoons,
  topBarLoading,
  error,
  canLoadMoreFeed,
  canLoadMoreAllCartoons,
  isAllCartoonsSheetOpen,
  refresh,
  fetchNext,
  openAllCartoonsSheet,
  closeAllCartoonsSheet,
  loadMoreAllCartoons,
} = useFollowPage()
const { isVisible: isLoadMoreVisible } = useElementVisibility(loadMoreTarget, {
  rootMargin: runtimeBudget.isConstrained ? '240px 0px' : '420px 0px',
  threshold: 0,
})
const isLoggedIn = computed(() => userStore.isLoggedIn())
const showInitialSkeleton = computed(() => loadingInitial.value && feedItems.value.length === 0)
const showInitialError = computed(
  () => feedItems.value.length === 0 && !loadingInitial.value && Boolean(error.value),
)
const showEmpty = computed(
  () => !loadingInitial.value && !error.value && feedItems.value.length === 0,
)
const showState = computed(() => showEmpty.value || showInitialError.value)
const showHeaderStrip = computed(() => !showEmpty.value && !showInitialError.value)
const emptyTitle = '没有关注内容，为你推荐以下角色'
const columnCount = computed(() => resolveSummaryContentColumnCount(screenWidth.value))
const gridStyle = computed(() => ({ '--follow-grid-columns': String(columnCount.value) }))
const feedColumns = computed(() => distributeWorks(feedItems.value, columnCount.value))
const skeletonColumns = computed(() =>
  distributeByIndex(
    Array.from({ length: Math.max(10, columnCount.value * 2) }, (_, index) => index),
    columnCount.value,
  ),
)

function distributeByIndex<T>(items: T[], count: number): T[][] {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as T[])

  items.forEach((item, index) => {
    columns[index % columns.length]?.push(item)
  })

  return columns
}

function estimateWorkHeight(item: UserWorkItem) {
  const kind = resolveSummaryContentKind(item)

  if (kind === 'radio') {
    return 1.38
  }

  if (kind === 'media-imgtxt') {
    return 1.72
  }

  return 2.0
}

function distributeWorks(items: UserWorkItem[], count: number) {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as UserWorkItem[])
  const heights = Array.from({ length: columns.length }, () => 0)

  items.forEach((item) => {
    const targetIndex = heights.indexOf(Math.min(...heights))
    const nextHeight = (heights[targetIndex] ?? 0) + estimateWorkHeight(item)

    columns[targetIndex]?.push(item)
    heights[targetIndex] = nextHeight
  })

  return columns
}

function openWork(item: UserWorkItem) {
  const url = buildSummaryContentViewUrl(item, themeStore.currentTheme)

  if (url) {
    openLegacyPlayerUrl(url)
  }
}

function openAuthor(item: UserWorkItem) {
  const uid = item.user?.uid

  if (!uid) {
    return
  }

  void router.push(`/${uid}`)
}

function openCartoon(item: FollowCartoonItem | FollowCartoonDetailItem | { id: string }) {
  const url = buildCartoonUrl(item)

  if (!url) {
    return
  }

  closeAllCartoonsSheet()
  void router.push(url)
}

async function handleFollowCartoon(item: FollowCartoonItem) {
  if (!isLoggedIn.value) {
    return
  }

  if (followLoadingIds.value.includes(item.id)) {
    return
  }

  followLoadingIds.value = [...followLoadingIds.value, item.id]

  try {
    await followCartoon(item.id)
    topCartoons.value = topCartoons.value.map((current) =>
      current.id === item.id
        ? {
            ...current,
            hasFollow: 1,
          }
        : current,
    )
    toast.success('已关注')
    await refresh()
  } catch (err) {
    toast.info(err instanceof Error ? err.message : '关注状态更新失败')
  } finally {
    followLoadingIds.value = followLoadingIds.value.filter((id) => id !== item.id)
  }
}

watch([isLoadMoreVisible, canLoadMoreFeed], ([visible, canLoadMore]) => {
  if (visible && canLoadMore) {
    void fetchNext()
  }
})

watch(
  () => isAllCartoonsSheetOpen.value,
  (open) => {
    if (!open) {
      document.body.style.removeProperty('overflow')
      return
    }

    document.body.style.overflow = 'hidden'
  },
)

onBeforeUnmount(() => {
  document.body.style.removeProperty('overflow')
})

applyDocumentTitle('我的关注')

useRouteRefreshSignal('follow', async () => {
  if (!isLoggedIn.value) {
    return
  }

  await refresh()
})

watch(
  isLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      void refresh()
    }
  },
  { immediate: true },
)
</script>

<template>
  <section class="follow-page">
    <div class="follow-page__inner">
      <LoginPrompt v-if="!isLoggedIn" message="登录后查看关注" />

      <template v-else>
        <FollowCartoonStrip
          v-if="showHeaderStrip"
          :cartoons="topCartoons"
          :loading="topBarLoading"
          :follow-loading-ids="followLoadingIds"
          @cartoon-click="openCartoon"
          @follow-click="handleFollowCartoon"
          @all-click="openAllCartoonsSheet"
        />

        <div
          v-if="showInitialSkeleton"
          class="follow-page__grid"
          :style="gridStyle"
          aria-label="关注内容加载中"
        >
          <div
            v-for="(column, columnIndex) in skeletonColumns"
            :key="`skeleton-${columnIndex}`"
            class="follow-page__column"
          >
            <div
              v-for="item in column"
              :key="`skeleton-card-${item}`"
              class="follow-page__card-skeleton"
            >
              <div
                class="follow-page__cover-skeleton"
                :class="{ 'is-tall': item % 3 !== 1, 'is-short': item % 3 === 1 }"
              />
              <div class="follow-page__body-skeleton">
                <span class="follow-page__line-skeleton is-title" />
                <span class="follow-page__line-skeleton is-author" />
                <span class="follow-page__line-skeleton is-stats" />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="showState">
          <div v-if="showEmpty" class="follow-page__empty-shell">
            <div class="follow-page__empty-copy">
              <img
                class="follow-page__empty-mascot"
                :src="stateImages.emptyMascot"
                alt="推荐角色"
              />
              <h2 class="follow-page__empty-title">{{ emptyTitle }}</h2>
            </div>

            <div class="follow-page__empty-strip">
              <FollowCartoonStrip
                :cartoons="topCartoons"
                :loading="topBarLoading"
                :follow-loading-ids="followLoadingIds"
                layout="grid"
                :show-all-action="false"
                @cartoon-click="openCartoon"
                @follow-click="handleFollowCartoon"
                @all-click="openAllCartoonsSheet"
              />
            </div>
          </div>

          <AppErrorState v-else variant="full" @retry="refresh" />
        </div>

        <div v-else class="follow-page__grid" :style="gridStyle">
          <div
            v-for="(column, columnIndex) in feedColumns"
            :key="`follow-column-${columnIndex}`"
            class="follow-page__column"
          >
            <SummaryContentCard
              v-for="(item, itemIndex) in column"
              :key="item.id"
              :item="item"
              :priority="columnIndex < 2 && itemIndex < 2"
              @click="openWork"
              @author-click="openAuthor"
            />
          </div>
        </div>

        <div
          v-if="feedItems.length > 0"
          ref="loadMoreTarget"
          class="follow-page__load-more"
          aria-hidden="true"
        >
          <span v-if="loadingMore">正在加载更多关注更新...</span>
        </div>
      </template>
    </div>

    <FollowCartoonSheet
      v-if="isLoggedIn"
      :open="isAllCartoonsSheetOpen"
      :items="allFollowedCartoons"
      :loading="loadingAllCartoons"
      :loading-more="loadingMoreAllCartoons"
      :has-next="canLoadMoreAllCartoons"
      @close="closeAllCartoonsSheet"
      @load-more="loadMoreAllCartoons"
      @cartoon-click="openCartoon"
      @work-click="openWork"
    />
  </section>
</template>

<style scoped>
.follow-page {
  min-width: 0;
}

.follow-page__inner {
  display: flex;
  min-height: calc(100vh - 16px);
  flex-direction: column;
  width: 100%;
}

.follow-page__grid {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(var(--follow-grid-columns), minmax(0, 1fr));
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.follow-page__column {
  min-width: 0;
}

.follow-page__card-skeleton {
  display: inline-flex;
  width: 100%;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 12px 12px 16px;
  break-inside: avoid;
}

.follow-page__cover-skeleton,
.follow-page__line-skeleton {
  position: absolute;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.follow-page__cover-skeleton::before,
.follow-page__line-skeleton::before {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--app-skeleton-shimmer) 50%,
    transparent 100%
  );
  animation: follow-page-shimmer 1.4s linear infinite;
  content: '';
}

.follow-page__cover-skeleton {
  position: relative;
  width: 100%;
  border-radius: 12px;
}

.follow-page__cover-skeleton.is-tall {
  aspect-ratio: 293 / 468.8;
}

.follow-page__cover-skeleton.is-short {
  aspect-ratio: 220 / 293.3333;
}

.follow-page__body-skeleton {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 0;
}

.follow-page__line-skeleton {
  position: relative;
  display: block;
  height: 12px;
  border-radius: 999px;
}

.follow-page__line-skeleton.is-title {
  width: 78%;
  height: 18px;
}

.follow-page__line-skeleton.is-author {
  width: 42%;
}

.follow-page__line-skeleton.is-stats {
  width: 36%;
}

.follow-page__load-more {
  display: flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  font-size: 13px;
  line-height: 18px;
}

.follow-page__empty-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 112px);
  gap: 12px;
  padding: 48px 12px 56px;
  box-sizing: border-box;
}

.follow-page__empty-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  text-align: center;
}

.follow-page__empty-mascot {
  width: var(--app-state-illustration-size-large);
  height: var(--app-state-illustration-size-large);
  object-fit: contain;
}

.follow-page__empty-title {
  margin: 0;
  color: var(--app-text-main);
  font-size: 19px;
  font-weight: 400;
  line-height: normal;
}

.follow-page__empty-strip {
  width: 100%;
  margin-top: 4px;
}

@keyframes follow-page-shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

@media (max-width: 640px) {
  .follow-page__empty-shell {
    min-height: auto;
    gap: 6px;
    padding: 16px 0 24px;
  }

  .follow-page__empty-copy {
    gap: 8px;
    padding: 0 16px 8px;
  }

  .follow-page__empty-mascot {
    width: var(--app-state-illustration-size);
    height: var(--app-state-illustration-size);
  }

  .follow-page__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .follow-page__card-skeleton {
    padding: 7px 5px 14px;
  }

  .follow-page__cover-skeleton {
    border-radius: 10px;
  }

  .follow-page__body-skeleton {
    padding: 7px 2px 0;
  }
}
</style>
